import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Custom Cursor Component - Apple Style
const CustomCursor = () => {
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  
  useEffect(() => {
    const cursor = cursorRef.current;
    const cursorDot = cursorDotRef.current;
    
    const moveCursor = (e) => {
      gsap.to(cursor, {
        x: e.clientX,
        y: e.clientY,
        duration: 0.5,
        ease: 'power2.out'
      });
      gsap.to(cursorDot, {
        x: e.clientX,
        y: e.clientY,
        duration: 0.1
      });
    };
    
    const handleMouseEnter = () => {
      gsap.to(cursor, { scale: 1.5, opacity: 0.5 });
    };
    
    const handleMouseLeave = () => {
      gsap.to(cursor, { scale: 1, opacity: 1 });
    };
    
    window.addEventListener('mousemove', moveCursor);
    
    // Add hover effect to interactive elements
    const interactiveElements = document.querySelectorAll('a, button, .service-card, .audit-card, .pack-card, .nav-item');
    interactiveElements.forEach(el => {
      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);
    });
    
    return () => {
      window.removeEventListener('mousemove', moveCursor);
      interactiveElements.forEach(el => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, []);
  
  return (
    <>
      <div ref={cursorRef} className="custom-cursor"></div>
      <div ref={cursorDotRef} className="custom-cursor-dot"></div>
    </>
  );
};

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <button 
      className={`back-to-top ${isVisible ? 'visible' : ''}`}
      onClick={scrollToTop}
      aria-label="Retour en haut"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M18 15l-6-6-6 6"/>
      </svg>
    </button>
  );
};

// Section Dots Navigation
const SectionDots = ({ activeSection }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <div className="section-dots">
      {navItems.map((item) => (
        <button
          key={item.id}
          className={`section-dot ${activeSection === item.id ? 'active' : ''}`}
          onClick={() => scrollToSection(item.id)}
          aria-label={item.label}
        >
          <span className="dot-tooltip">{item.label}</span>
        </button>
      ))}
    </div>
  );
};

// Navigation Menu Items
const navItems = [
  { id: 'hero', label: 'Accueil', icon: 'home' },
  { id: 'overview', label: 'Entreprise', icon: 'building' },
  { id: 'audit', label: 'Audit', icon: 'chart' },
  { id: 'improvements', label: 'Améliorations', icon: 'arrow-up' },
  { id: 'offer', label: 'Services', icon: 'grid' },
  { id: 'case-study', label: 'Cas Client', icon: 'star' },
  { id: 'pricing', label: 'Tarifs', icon: 'tag' },
  { id: 'contact', label: 'Contact', icon: 'mail' }
];

// Navigation Icons
const NavIcon = ({ type }) => {
  const icons = {
    home: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    ),
    building: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
        <line x1="9" y1="6" x2="9" y2="6"></line>
        <line x1="15" y1="6" x2="15" y2="6"></line>
        <line x1="9" y1="10" x2="9" y2="10"></line>
        <line x1="15" y1="10" x2="15" y2="10"></line>
        <line x1="9" y1="14" x2="9" y2="14"></line>
        <line x1="15" y1="14" x2="15" y2="14"></line>
        <line x1="9" y1="18" x2="15" y2="18"></line>
      </svg>
    ),
    chart: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"></line>
        <line x1="12" y1="20" x2="12" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="14"></line>
      </svg>
    ),
    'arrow-up': (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5"></line>
        <polyline points="5 12 12 5 19 12"></polyline>
      </svg>
    ),
    grid: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
    ),
    star: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
      </svg>
    ),
    tag: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
        <line x1="7" y1="7" x2="7.01" y2="7"></line>
      </svg>
    ),
    mail: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </svg>
    )
  };
  return icons[type] || null;
};

// Floating Navigation Component
const FloatingNav = ({ activeSection, onNavigate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    // Show nav after scrolling past hero
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (navRef.current) {
      gsap.to(navRef.current, {
        y: isVisible ? 0 : -100,
        opacity: isVisible ? 1 : 0,
        duration: 0.4,
        ease: 'power2.out'
      });
    }
  }, [isVisible]);

  return (
    <nav 
      ref={navRef}
      className={`floating-nav ${isExpanded ? 'expanded' : ''}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      data-testid="floating-nav"
    >
      <div className="nav-container">
        {/* Logo mini */}
        <div className="nav-logo" onClick={() => onNavigate('hero')}>
          <span>L</span>
        </div>
        
        {/* Navigation items */}
        <div className="nav-items">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => onNavigate(item.id)}
              data-testid={`nav-${item.id}`}
            >
              <span className="nav-icon">
                <NavIcon type={item.icon} />
              </span>
              <span className="nav-label">{item.label}</span>
              {activeSection === item.id && <span className="nav-indicator"></span>}
            </button>
          ))}
        </div>
        
        {/* CTA button */}
        <a href="mailto:info@lynk360.ch" className="nav-cta">
          <span>Contact</span>
        </a>
      </div>
    </nav>
  );
};

// Icons as SVG components
const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const MapPinIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
    <rect x="2" y="9" width="4" height="12"></rect>
    <circle cx="4" cy="4" r="2"></circle>
  </svg>
);

const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
    <circle cx="12" cy="13" r="4"></circle>
  </svg>
);

const MonitorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
    <line x1="8" y1="21" x2="16" y2="21"></line>
    <line x1="12" y1="17" x2="12" y2="21"></line>
  </svg>
);

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const AlertTriangleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Animated Text Component - Letter by letter reveal with color change
const AnimatedText = ({ text, className, trigger }) => {
  const textRef = useRef(null);
  
  useEffect(() => {
    if (!textRef.current) return;
    
    const chars = textRef.current.querySelectorAll('.char');
    
    gsap.fromTo(chars, 
      { 
        opacity: 0, 
        y: 100,
        rotateX: -90,
        color: '#000000'
      },
      {
        opacity: 1,
        y: 0,
        rotateX: 0,
        color: '#FF4400',
        duration: 0.8,
        stagger: 0.03,
        ease: 'back.out(1.7)',
        scrollTrigger: trigger ? {
          trigger: trigger,
          start: 'top 80%'
        } : undefined
      }
    );
  }, [trigger]);
  
  return (
    <span ref={textRef} className={className}>
      {text.split('').map((char, i) => (
        <span key={i} className="char" style={{ display: 'inline-block' }}>
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
};

// Magnetic Card Component - follows mouse
const MagneticCard = ({ children, className, intensity = 0.3 }) => {
  const cardRef = useRef(null);
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    gsap.to(cardRef.current, {
      rotateY: x * intensity * 0.1,
      rotateX: -y * intensity * 0.1,
      transformPerspective: 1000,
      duration: 0.5,
      ease: 'power2.out'
    });
  };
  
  const handleMouseLeave = () => {
    gsap.to(cardRef.current, {
      rotateY: 0,
      rotateX: 0,
      duration: 0.5,
      ease: 'power2.out'
    });
  };
  
  return (
    <div 
      ref={cardRef} 
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ transformStyle: 'preserve-3d' }}
    >
      {children}
    </div>
  );
};

// Score Ring Component with enhanced animation
const ScoreRing = ({ score, size = 80 }) => {
  const circumference = 2 * Math.PI * 35;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="score-ring-container" style={{ width: size, height: size }}>
      <svg className="score-ring" viewBox="0 0 80 80">
        <circle
          className="score-ring-bg"
          cx="40"
          cy="40"
          r="35"
          fill="none"
          strokeWidth="6"
        />
        <circle
          className="score-ring-progress"
          cx="40"
          cy="40"
          r="35"
          fill="none"
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          data-target={strokeDashoffset}
          transform="rotate(-90 40 40)"
        />
      </svg>
      <div className="score-value">
        <span className="counter" data-target={score}>0</span>
        <span>%</span>
      </div>
    </div>
  );
};

// Lynk Logo - Using official image asset
const LynkLogo = ({ variant = "dark" }) => {
  if (variant === "light") {
    return (
      <img 
        src="https://customer-assets.emergentagent.com/job_fcf7de87-5a39-4d29-9f79-256b8a038a00/artifacts/493pipc9_LYNK%20RGB%20BLANC%20150.png"
        alt="Lynk Logo"
        className="lynk-logo"
        style={{ objectFit: 'contain' }}
      />
    );
  }
  
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/lmevuxzb_Fichier%201%40300x.png"
      alt="Lynk Logo"
      className="lynk-logo"
      style={{ objectFit: 'contain' }}
    />
  );
};

// Elegant mini icons for case study stats
const MiniSmartphoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
    <line x1="12" y1="18" x2="12.01" y2="18"></line>
  </svg>
);

const MiniUsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const MiniCheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const MiniCameraIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
    <circle cx="12" cy="13" r="4"></circle>
  </svg>
);

const MiniImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21 15 16 10 5 21"></polyline>
  </svg>
);

const MiniSparklesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"></path>
  </svg>
);

const MiniPlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="10 8 16 12 10 16 10 8"></polygon>
  </svg>
);

const MiniVideoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"></polygon>
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
  </svg>
);

const MiniBrushIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
    <path d="M2 2l7.586 7.586"></path>
    <circle cx="11" cy="11" r="2"></circle>
  </svg>
);

const MiniMapPinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const MiniMailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const MiniTargetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const MiniHomeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

// Data
const auditCards = [
  {
    title: "Site Web",
    icon: GlobeIcon,
    score: 55,
    status: "warning",
    metrics: [
      { positive: true, text: "EcoIndex : Grade A" },
      { positive: false, text: "Design : À moderniser" },
      { positive: false, text: "SEO : Sous-exploité" }
    ]
  },
  {
    title: "Google My Business",
    icon: MapPinIcon,
    score: 70,
    status: "warning",
    metrics: [
      { positive: true, text: "Note : 5.0 ★" },
      { positive: false, text: "Avis : 4 avis seulement" },
      { positive: true, text: "Fiche : Revendiquée" }
    ]
  },
  {
    title: "Instagram",
    icon: InstagramIcon,
    score: 45,
    status: "warning",
    metrics: [
      { positive: true, text: "Publications : 57 posts" },
      { positive: false, text: "Followers : 168" },
      { positive: false, text: "Contenu : IA trop visible" }
    ]
  },
  {
    title: "Facebook",
    icon: FacebookIcon,
    score: 35,
    status: "danger",
    metrics: [
      { positive: false, text: "Followers : 104" },
      { positive: false, text: "Avis : 0 avis" },
      { positive: false, text: "Engagement : Très faible" }
    ]
  },
  {
    title: "LinkedIn",
    icon: LinkedInIcon,
    score: 0,
    status: "danger",
    metrics: [
      { positive: false, text: "Page : Inexistante" },
      { positive: false, text: "Visibilité B2B : Nulle" },
      { positive: false, text: "Impact : Négatif" }
    ]
  }
];

const improvements = {
  high: [
    { title: "Créer une page LinkedIn", desc: "Indispensable pour la crédibilité B2B et le networking professionnel dans l'immobilier" },
    { title: "Augmenter les avis Google", desc: "4 avis insuffisants — objectif 50+ avis pour dominer le SEO local à Saint-Sulpice" },
    { title: "Développer l'engagement", desc: "Likes, commentaires et partages quasi inexistants sur toutes les plateformes" }
  ],
  medium: [
    { title: "Réduire l'aspect IA du contenu", desc: "Visuels trop génériques et répétitifs, manque d'authenticité et de touche humaine" },
    { title: "Augmenter la communauté", desc: "168 followers Instagram après 9 ans d'activité — potentiel de croissance énorme" }
  ],
  low: [
    { title: "Moderniser le design du site", desc: "Interface datée, expérience utilisateur à repenser pour 2025" },
    { title: "Créer une stratégie de contenu", desc: "Publications sans fil rouge éditorial — besoin d'un calendrier cohérent" }
  ]
};

const services = [
  { title: "Gestion réseaux sociaux", icon: UsersIcon, desc: "Animation quotidienne de vos communautés Instagram, Facebook et LinkedIn" },
  { title: "Création de contenu", icon: CameraIcon, desc: "De la photo professionnelle à la vidéo immobilière avec IA avancée" },
  { title: "Gestion site web", icon: MonitorIcon, desc: "Mises à jour continues, optimisations techniques et design moderne" },
  { title: "E-réputation", icon: ShieldIcon, desc: "Réponses aux avis, alertes via Mention, gestion de crise" },
  { title: "Mailing", icon: MailIcon, desc: "Base Lynk exclusive de 7'000+ contacts qualifiés sur le canton de Vaud" },
  { title: "SEO / SEA", icon: SearchIcon, desc: "Référencement naturel et campagnes publicitaires Google & Meta" }
];

const packs = [
  {
    name: "Silver",
    price: "6'000.-",
    popular: false,
    headerClass: "silver",
    included: [
      "1 post + 1 story / semaine",
      "Création de contenus (photos + visuels)",
      "Budget Meta Ads 100.-/mois inclus",
      "E-réputation (monitoring)",
      "Reporting trimestriel"
    ],
    notIncluded: [
      "Vidéos IA avancées",
      "Gestion site web",
      "Mailing 7'000 contacts"
    ]
  },
  {
    name: "Gold",
    price: "10'000.-",
    popular: true,
    headerClass: "gold",
    included: [
      "2 posts + 4 stories / semaine",
      "Création contenus + vidéos IA",
      "Budget Meta Ads 200.-/mois inclus",
      "E-réputation + réponses aux avis",
      "Gestion site web (MAJ mensuelles)",
      "Reporting mensuel",
      "Vidéos IA avancées"
    ],
    notIncluded: [
      "Mailing 7'000 contacts"
    ]
  },
  {
    name: "Diamond",
    price: "15'000.-",
    popular: false,
    headerClass: "diamond",
    included: [
      "3 posts + stories illimitées",
      "Contenus premium + IA avancée",
      "Budget Meta Ads 300.-/mois inclus",
      "E-réputation + alertes Mention",
      "Site web + optimisations SEO",
      "Reporting mensuel + call stratégique",
      "Vidéos IA avancées illimitées",
      "Mailing 7'000 contacts VD"
    ],
    notIncluded: []
  }
];

function App() {
  const heroRef = useRef(null);
  const particlesRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  // Smooth scroll to section
  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for floating nav
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: sectionId === 'hero' ? 0 : offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    const sections = navItems.map(item => item.id);
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Preloader
    const preloadTimer = setTimeout(() => setIsLoaded(true), 500);
    
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      gsap.set('.hero-content > *, .section-header, .card, .improvement-item, .service-card, .pack-card, .cta-content > *', {
        opacity: 1,
        y: 0,
        scale: 1
      });
      return;
    }

    // Create floating particles with enhanced animation
    const particlesContainer = particlesRef.current;
    if (particlesContainer) {
      for (let i = 0; i < 40; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;
        const size = 4 + Math.random() * 8;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particlesContainer.appendChild(particle);
        
        gsap.to(particle, {
          y: -150 - Math.random() * 300,
          x: (Math.random() - 0.5) * 150,
          opacity: 0,
          scale: 0,
          duration: 4 + Math.random() * 5,
          repeat: -1,
          delay: Math.random() * 4,
          ease: "power1.out"
        });
      }
    }

    // Progress bar animation
    gsap.to('.progress-bar', {
      width: '100%',
      ease: 'none',
      scrollTrigger: {
        trigger: 'body',
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.3
      }
    });

    // HERO CINEMATIC - Zoom out effect on scroll
    gsap.to('.hero-content', {
      scale: 0.8,
      opacity: 0,
      y: -100,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });

    // Hero background text parallax with zoom - ENHANCED
    gsap.to('.hero-bg-text', {
      x: '-30%',
      scale: 1.8,
      opacity: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });

    // Second background text - opposite direction
    gsap.to('.hero-bg-text-2', {
      x: '20%',
      scale: 1.5,
      opacity: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });

    // Hero intro timeline with enhanced effects
    gsap.set(['.lynk-logo', '.hero-tagline', '.hero-title', '.hero-subtitle', '.hero-description', '.scroll-indicator'], {
      opacity: 0,
      y: 50
    });
    gsap.set('.lynk-logo', { scale: 0.5, rotateY: -30 });
    gsap.set('.hero-subtitle', { scale: 0.5 });
    gsap.set('.hero-title', { scale: 1.5 });

    const heroTl = gsap.timeline({ delay: 0.5 });
    heroTl
      .to('.lynk-logo', { scale: 1, rotateY: 0, opacity: 1, y: 0, duration: 1.2, ease: 'elastic.out(1, 0.5)' })
      .to('.hero-tagline', { y: 0, opacity: 1, duration: 0.6 }, '-=0.6')
      .to('.hero-title', { scale: 1, y: 0, opacity: 1, duration: 1, ease: 'power4.out' }, '-=0.4')
      .to('.hero-subtitle', { scale: 1, opacity: 1, y: 0, duration: 0.8, ease: 'back.out(2)' }, '-=0.5')
      .to('.hero-description', { y: 0, opacity: 1, duration: 0.6 }, '-=0.3')
      .to('.scroll-indicator', { y: 0, opacity: 1, duration: 0.6 }, '-=0.2');

    // Scroll indicator enhanced pulse
    gsap.to('.scroll-line', {
      scaleY: 2,
      opacity: 0.3,
      duration: 1.2,
      repeat: -1,
      yoyo: true,
      ease: 'power2.inOut'
    });

    // Section animations with ScrollTrigger - ENHANCED
    const sections = document.querySelectorAll('.section');
    sections.forEach(section => {
      const header = section.querySelector('.section-header');
      if (header) {
        // Zoom in effect for section headers
        gsap.fromTo(header, 
          { scale: 0.8, opacity: 0, y: 100 },
          { 
            scale: 1,
            y: 0, 
            opacity: 1, 
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 85%'
            }
          }
        );
      }

      // Decorative line with enhanced animation
      const decorLine = section.querySelector('.decor-line');
      if (decorLine) {
        gsap.fromTo(decorLine, 
          { width: 0, opacity: 0 },
          { 
            width: 80, 
            opacity: 1,
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 80%'
            }
          }
        );
      }
    });

    // Overview card animation - 3D flip in
    gsap.fromTo('.overview-card', 
      { rotateY: -45, opacity: 0, x: -100 },
      {
        rotateY: 0,
        x: 0,
        opacity: 1,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.overview',
          start: 'top 80%'
        }
      }
    );

    gsap.fromTo('.info-item', 
      { x: -50, opacity: 0, scale: 0.8 },
      {
        x: 0,
        opacity: 1,
        scale: 1,
        duration: 0.6,
        stagger: 0.15,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: '.overview-card',
          start: 'top 75%'
        }
      }
    );

    gsap.fromTo('.service-tag', 
      { scale: 0, opacity: 0, rotation: -10 },
      {
        scale: 1,
        opacity: 1,
        rotation: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: 'elastic.out(1, 0.5)',
        scrollTrigger: {
          trigger: '.service-tags',
          start: 'top 85%'
        }
      }
    );

    // Audit cards animation - Staggered zoom with rotation
    gsap.fromTo('.audit-card', 
      { scale: 0.5, opacity: 0, rotateY: 30, y: 100 },
      {
        scale: 1,
        rotateY: 0,
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.audit-grid',
          start: 'top 80%'
        }
      }
    );

    // Score rings animation - Enhanced
    const scoreRings = document.querySelectorAll('.score-ring-progress');
    scoreRings.forEach(ring => {
      const target = ring.getAttribute('data-target');
      gsap.to(ring, {
        strokeDashoffset: target,
        duration: 2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ring.closest('.audit-card'),
          start: 'top 80%'
        }
      });
    });

    // Counter animations - Slot machine effect
    const counters = document.querySelectorAll('.counter');
    counters.forEach(counter => {
      const target = parseInt(counter.getAttribute('data-target'));
      const obj = { value: 0 };
      
      // Add scramble effect before final value
      gsap.to(obj, {
        value: target,
        duration: 2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: counter.closest('.audit-card') || counter.closest('.metric-card') || counter.closest('.global-score'),
          start: 'top 85%'
        },
        onUpdate: () => {
          counter.textContent = Math.round(obj.value);
        }
      });
    });

    // Audit metrics - Slide in with color transition
    gsap.fromTo('.audit-metric', 
      { x: -40, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.audit-grid',
          start: 'top 70%'
        }
      }
    );

    // Global score badge - Dramatic entrance
    gsap.fromTo('.global-score', 
      { scale: 0, opacity: 0, rotation: -180 },
      {
        scale: 1,
        rotation: 0,
        opacity: 1,
        duration: 1.2,
        ease: 'elastic.out(1, 0.5)',
        scrollTrigger: {
          trigger: '.global-score',
          start: 'top 85%'
        }
      }
    );

    // Improvements section - Slide and fade with depth
    gsap.fromTo('.priority-group', 
      { x: -100, opacity: 0, scale: 0.9 },
      {
        x: 0,
        scale: 1,
        opacity: 1,
        duration: 0.8,
        stagger: 0.25,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.improvements',
          start: 'top 80%'
        }
      }
    );

    gsap.fromTo('.improvement-item', 
      { x: -60, opacity: 0, rotateX: -20 },
      {
        x: 0,
        rotateX: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.12,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.improvements',
          start: 'top 70%'
        }
      }
    );

    // Services cards - 3D flip entrance
    gsap.fromTo('.service-card', 
      { rotateX: 90, y: 80, opacity: 0 },
      {
        rotateX: 0,
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.services-grid',
          start: 'top 80%'
        }
      }
    );

    gsap.fromTo('.certification', 
      { scale: 0, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 0.6,
        stagger: 0.2,
        ease: 'elastic.out(1, 0.5)',
        scrollTrigger: {
          trigger: '.certifications',
          start: 'top 85%'
        }
      }
    );

    // Case study - Dramatic zoom in
    gsap.fromTo('.case-service-block', 
      { scale: 0.9, opacity: 0, y: 60 },
      {
        scale: 1,
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.case-study',
          start: 'top 80%'
        }
      }
    );

    gsap.fromTo('.result-item', 
      { scale: 0, opacity: 0, rotation: -10 },
      {
        scale: 1,
        rotation: 0,
        opacity: 1,
        duration: 0.7,
        stagger: 0.15,
        ease: 'elastic.out(1, 0.5)',
        scrollTrigger: {
          trigger: '.results-grid',
          start: 'top 85%'
        }
      }
    );

    // Pricing packs - Staggered zoom with bounce
    gsap.fromTo('.pack-card', 
      { scale: 0.7, opacity: 0, y: 80 },
      {
        scale: 1,
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: '.packs-grid',
          start: 'top 80%'
        }
      }
    );

    // Pack features - Cascade reveal
    gsap.fromTo('.feature-item', 
      { x: -30, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.4,
        stagger: 0.05,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.packs-grid',
          start: 'top 70%'
        }
      }
    );

    // Case header card animation
    gsap.fromTo('.case-header-card',
      { y: 60, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.case-study',
          start: 'top 75%'
        }
      }
    );

    // Case results card animation
    gsap.fromTo('.case-results-card',
      { y: 60, opacity: 0, scale: 0.95 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.case-results-card',
          start: 'top 85%'
        }
      }
    );

    // CTA section - Dramatic timeline
    gsap.fromTo('.cta h2',
      { opacity: 0, y: 50, scale: 0.9 },
      { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'power3.out',
        scrollTrigger: { trigger: '.cta', start: 'top 75%' }
      }
    );
    gsap.fromTo('.cta-subtitle',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.6, delay: 0.2,
        scrollTrigger: { trigger: '.cta', start: 'top 75%' }
      }
    );
    gsap.fromTo('.cta-buttons',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.6, delay: 0.35,
        scrollTrigger: { trigger: '.cta', start: 'top 75%' }
      }
    );
    gsap.fromTo('.cta-footer',
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.6, delay: 0.5,
        scrollTrigger: { trigger: '.cta', start: 'top 75%' }
      }
    );

    // Refresh ScrollTrigger after layout is settled
    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 1000);

    // Cleanup
    return () => {
      clearTimeout(preloadTimer);
      clearTimeout(refreshTimer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className={`app ${isLoaded ? 'loaded' : ''}`} data-testid="app-container">
      {/* Custom Cursor - Desktop only */}
      <CustomCursor />
      
      {/* Section Dots Navigation */}
      <SectionDots activeSection={activeSection} />
      
      {/* Back to Top Button */}
      <BackToTop />
      
      {/* Floating Navigation */}
      <FloatingNav activeSection={activeSection} onNavigate={scrollToSection} />
      
      {/* Preloader */}
      <div className={`preloader ${isLoaded ? 'hidden' : ''}`}>
        <div className="preloader-content">
          <div className="preloader-spinner"></div>
          <span>Chargement...</span>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="progress-bar" data-testid="progress-bar"></div>

      {/* Section 1: Hero */}
      <section className="hero" id="hero" ref={heroRef} data-testid="hero-section">
        <div className="hero-gradient-overlay"></div>
        <div className="hero-bg-text">AUDIT</div>
        <div className="hero-bg-text hero-bg-text-2">DIGITAL</div>
        <div className="particles" ref={particlesRef}></div>
        <div className="hero-content">
          <LynkLogo variant="dark" />
          <p className="hero-tagline">Agence Digitale 360°</p>
          <h1 className="hero-title">
            <span className="title-line">Audit Digital</span>
          </h1>
          <p className="hero-subtitle">Gerancimo</p>
          <p className="hero-description">Analyse complète de votre présence digitale et recommandations stratégiques</p>
          <div className="scroll-indicator" data-testid="scroll-indicator" onClick={() => scrollToSection('overview')} style={{ cursor: 'pointer' }}>
            <div className="scroll-line"></div>
            <span>Découvrir</span>
          </div>
        </div>
      </section>

      {/* Section 2: Company Overview */}
      <section className="section overview" id="overview" data-testid="overview-section">
        <div className="container">
          <MagneticCard className="overview-card" intensity={0.2}>
            <div className="company-header">
              <div className="company-logo">
                <img src="https://customer-assets.emergentagent.com/job_fcf7de87-5a39-4d29-9f79-256b8a038a00/artifacts/w4pgtown_image.png" alt="Gerancimo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 'inherit' }} />
              </div>
              <div className="company-info">
                <h2>Gerancimo Sàrl</h2>
                <p>Agence immobilière depuis 2016</p>
              </div>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <MapPinIcon />
                <span>Rue du Centre 42E, 1025 Saint-Sulpice VD</span>
              </div>
              <div className="info-item">
                <GlobeIcon />
                <span>gerancimo.ch</span>
              </div>
              <div className="info-item">
                <PhoneIcon />
                <span>+41 21 614 00 30</span>
              </div>
              <div className="info-item">
                <MailIcon />
                <span>info@gerancimo.ch</span>
              </div>
            </div>
            <div className="service-tags">
              {['Gérance', 'Location', 'Vente', 'PPE', 'Transmission de commerce'].map((tag, i) => (
                <span key={i} className="service-tag">{tag}</span>
              ))}
            </div>
          </MagneticCard>
        </div>
      </section>

      {/* Section 3: Audit State */}
      <section className="section audit" id="audit" data-testid="audit-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Analyse</span>
            <h2>État des lieux</h2>
            <p>Évaluation détaillée de chaque canal digital</p>
            <div className="decor-line"></div>
          </div>

          <div className="audit-grid">
            {auditCards.map((card, i) => (
              <MagneticCard key={i} className={`audit-card status-${card.status}`} data-testid={`audit-card-${i}`} intensity={0.15}>
                <div className="audit-card-header">
                  <div className="audit-icon">
                    <card.icon />
                  </div>
                  <ScoreRing score={card.score} />
                </div>
                <h3>{card.title}</h3>
                <div className="audit-metrics">
                  {card.metrics.map((metric, j) => (
                    <div key={j} className={`audit-metric ${metric.positive ? 'positive' : 'negative'}`}>
                      {metric.positive ? <CheckIcon /> : <XIcon />}
                      <span>{metric.text}</span>
                    </div>
                  ))}
                </div>
              </MagneticCard>
            ))}
          </div>

          <div className="global-score" data-testid="global-score">
            <span className="global-score-label">Score digital global</span>
            <span className="global-score-value">
              <span className="counter" data-target="41">0</span>%
            </span>
            <div className="shimmer"></div>
          </div>
        </div>
      </section>

      {/* Section 4: Improvements */}
      <section className="section improvements" id="improvements" data-testid="improvements-section">
        <div className="container">
          <div className="section-header light">
            <span className="section-label">Recommandations</span>
            <h2>À améliorer</h2>
            <p>Actions prioritaires pour booster votre présence digitale</p>
          </div>

          <div className="priority-groups">
            <div className="priority-group high">
              <div className="priority-header">
                <div className="priority-icon">
                  <AlertTriangleIcon />
                </div>
                <h3>Priorité Haute</h3>
              </div>
              <div className="priority-items">
                {improvements.high.map((item, i) => (
                  <div key={i} className="improvement-item">
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="priority-group medium">
              <div className="priority-header">
                <div className="priority-icon">
                  <AlertTriangleIcon />
                </div>
                <h3>Priorité Moyenne</h3>
              </div>
              <div className="priority-items">
                {improvements.medium.map((item, i) => (
                  <div key={i} className="improvement-item">
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="priority-group low">
              <div className="priority-header">
                <div className="priority-icon">
                  <AlertTriangleIcon />
                </div>
                <h3>Priorité Basse</h3>
              </div>
              <div className="priority-items">
                {improvements.low.map((item, i) => (
                  <div key={i} className="improvement-item">
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Our Offer */}
      <section className="section offer" id="offer" data-testid="offer-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Services</span>
            <h2>Notre offre</h2>
            <p>Solutions digitales sur mesure pour l'immobilier</p>
            <div className="decor-line"></div>
          </div>

          <div className="services-grid">
            {services.map((service, i) => (
              <MagneticCard key={i} className="service-card" data-testid={`service-card-${i}`} intensity={0.25}>
                <div className="service-icon">
                  <service.icon />
                </div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </MagneticCard>
            ))}
          </div>

          <div className="certifications">
            <div className="certification">
              <img 
                src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/4y7odzk6_image.png" 
                alt="Google Ads Partner" 
                className="cert-logo"
              />
              <span>Certifié Google Partner</span>
            </div>
            <div className="certification">
              <img 
                src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/waswtuvf_image.png" 
                alt="Meta Business Partner" 
                className="cert-logo"
              />
              <span>Meta Business Partner</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Case Study - Amma Immo */}
      <section className="section case-study" id="case-study" data-testid="case-study-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Étude de cas</span>
            <h2>Amma Immo</h2>
            <p>Découvrez les résultats concrets de notre accompagnement digital</p>
            <div className="decor-line"></div>
          </div>

          {/* Amma Immo Header Card */}
          <div className="case-header-card">
            <div className="case-client-info">
              <div className="case-client-logo">
                <img src="https://customer-assets.emergentagent.com/job_fcf7de87-5a39-4d29-9f79-256b8a038a00/artifacts/b39ewg3j_image.png" alt="Amma Immo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
              </div>
              <div className="case-client-details">
                <h3>Amma Immo</h3>
                <p>Agence immobilière • Vaud, Fribourg et Valais</p>
                <a href="https://amma.immo" target="_blank" rel="noopener noreferrer" className="case-client-link">
                  amma.immo
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </a>
              </div>
            </div>
            <div className="case-metrics-summary">
              <div className="case-metric-item">
                <span className="case-metric-value"><span className="counter" data-target="127">0</span></span>
                <span className="case-metric-label">Publications</span>
              </div>
              <div className="case-metric-item">
                <span className="case-metric-value"><span className="counter" data-target="1599">0</span></span>
                <span className="case-metric-label">Followers</span>
              </div>
              <div className="case-metric-item">
                <span className="case-metric-value">+<span className="counter" data-target="850">0</span>%</span>
                <span className="case-metric-label">Croissance</span>
              </div>
            </div>
          </div>

          {/* Services Showcase */}
          <div className="case-services">
            
            {/* Service 1: Social Media */}
            <div className="case-service-block">
              <div className="case-service-header">
                <div className="case-service-icon">
                  <UsersIcon />
                </div>
                <div className="case-service-title">
                  <h4>Gestion des réseaux sociaux</h4>
                  <p>Création et animation de la présence Instagram</p>
                </div>
              </div>
              <div className="case-service-content">
                <div className="case-gallery social-gallery">
                  <div className="gallery-item phone-mockup">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/mwajrgby_image.png" 
                      alt="Profil Instagram Amma Immo"
                    />
                  </div>
                  <div className="gallery-item phone-mockup">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/i5o27mep_image.png" 
                      alt="Feed Instagram Amma Immo"
                    />
                  </div>
                </div>
                <div className="case-service-stats">
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniSmartphoneIcon /></span>
                    <span>127 publications créées</span>
                  </div>
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniUsersIcon /></span>
                    <span>1'599 followers acquis</span>
                  </div>
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniCheckCircleIcon /></span>
                    <span>Compte vérifié</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 2: Content Creation - Before/After Showcase */}
            <div className="case-service-block content-creation-block">
              <div className="case-service-header">
                <div className="case-service-icon">
                  <CameraIcon />
                </div>
                <div className="case-service-title">
                  <h4>Création de contenu</h4>
                  <p>De simples photos à des vidéos immersives grâce à l'IA</p>
                </div>
              </div>
              <div className="case-service-content">
                {/* Before/After Comparison */}
                <div className="before-after-container">
                  {/* AVANT - Photos */}
                  <div className="before-section">
                    <div className="ba-label before-label">
                      <span className="ba-icon"><MiniCameraIcon /></span>
                      <span>AVANT</span>
                    </div>
                    <div className="ba-card">
                      <div className="ba-header">Photos standards</div>
                      <div className="ba-content">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/9bwae8a7_PHOTOS.jpg" 
                          alt="Photos immobilières originales"
                        />
                      </div>
                      <div className="ba-caption">
                        <span><MiniImageIcon /></span> Photos statiques du bien
                      </div>
                    </div>
                  </div>

                  {/* Arrow / Transform indicator */}
                  <div className="transform-indicator">
                    <div className="transform-arrow">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                    <div className="transform-label">
                      <span className="transform-icon"><MiniSparklesIcon /></span>
                      <span>Transformation IA</span>
                    </div>
                  </div>

                  {/* APRÈS - Vidéo */}
                  <div className="after-section">
                    <div className="ba-label after-label">
                      <span className="ba-icon"><MiniPlayIcon /></span>
                      <span>APRÈS</span>
                    </div>
                    <div className="ba-card video-card">
                      <div className="ba-header">Vidéo immersive</div>
                      <div className="ba-content">
                        <div className="video-wrapper-ba">
                          <iframe 
                            src="https://www.youtube.com/embed/sxcoH2zgdCw" 
                            title="Vidéo Amma Immo par Lynk"
                            frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                            allowFullScreen
                          ></iframe>
                        </div>
                      </div>
                      <div className="ba-caption">
                        <span><MiniVideoIcon /></span> Visite virtuelle animée
                      </div>
                    </div>
                  </div>
                </div>

                {/* Process Steps */}
                <div className="content-process">
                  <div className="process-step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h5>Photos du bien</h5>
                      <p>Réception des photos standards</p>
                    </div>
                  </div>
                  <div className="process-connector"></div>
                  <div className="process-step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h5>Traitement IA</h5>
                      <p>Animation et effets immersifs</p>
                    </div>
                  </div>
                  <div className="process-connector"></div>
                  <div className="process-step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h5>Vidéo finale</h5>
                      <p>Contenu prêt à publier</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 3: Website */}
            <div className="case-service-block">
              <div className="case-service-header">
                <div className="case-service-icon">
                  <MonitorIcon />
                </div>
                <div className="case-service-title">
                  <h4>Gestion site web</h4>
                  <p>Design moderne et expérience utilisateur optimisée</p>
                </div>
              </div>
              <div className="case-service-content">
                <div className="case-gallery website-gallery">
                  <div className="gallery-item browser-mockup">
                    <div className="browser-header">
                      <div className="browser-dots">
                        <span></span><span></span><span></span>
                      </div>
                      <div className="browser-url">amma.immo</div>
                    </div>
                    <div className="browser-content">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/8fd4crvq_image.png" 
                        alt="Site web Amma Immo"
                      />
                    </div>
                  </div>
                </div>
                <div className="case-service-stats">
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniBrushIcon /></span>
                    <span>Design immersif avec vidéo</span>
                  </div>
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniMapPinIcon /></span>
                    <span>Vaud, Fribourg et Valais</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 4: Mailing */}
            <div className="case-service-block">
              <div className="case-service-header">
                <div className="case-service-icon">
                  <MailIcon />
                </div>
                <div className="case-service-title">
                  <h4>Campagnes mailing</h4>
                  <p>Newsletters et communications ciblées</p>
                </div>
              </div>
              <div className="case-service-content">
                <div className="case-gallery mailing-gallery dual-screens">
                  <div className="gallery-item phone-mockup">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/piv8qm7u_image.png" 
                      alt="Newsletter Amma Immo - Header"
                    />
                  </div>
                  <div className="gallery-item phone-mockup">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_audit-immo-vaud/artifacts/ib8y8abn_image.png" 
                      alt="Newsletter Amma Immo - Le Méridien"
                    />
                  </div>
                </div>
                <div className="case-service-stats">
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniMailIcon /></span>
                    <span>Templates personnalisés</span>
                  </div>
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniTargetIcon /></span>
                    <span>Ciblage précis</span>
                  </div>
                  <div className="stat-badge">
                    <span className="stat-icon"><MiniHomeIcon /></span>
                    <span>Mise en avant des biens</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Results Summary */}
          <div className="case-results-card">
            <h3>Résultats obtenus</h3>
            <div className="results-grid">
              <div className="result-item">
                <span className="result-value">+<span className="counter" data-target="850">0</span>%</span>
                <span className="result-label">Croissance followers</span>
              </div>
              <div className="result-item">
                <span className="result-value">x<span className="counter" data-target="4">0</span></span>
                <span className="result-label">Engagement rate</span>
              </div>
              <div className="result-item">
                <span className="result-value">+<span className="counter" data-target="200">0</span>%</span>
                <span className="result-label">Leads générés</span>
              </div>
              <div className="result-item">
                <span className="result-value"><span className="counter" data-target="100">0</span>%</span>
                <span className="result-label">Satisfaction client</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Section 7: Pricing */}
      <section className="section pricing" id="pricing" data-testid="pricing-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Tarifs</span>
            <h2>Nos packs</h2>
            <p>Des formules adaptées à vos ambitions</p>
            <div className="decor-line"></div>
          </div>

          <div className="packs-grid">
            {packs.map((pack, i) => (
              <MagneticCard key={i} className={`pack-card ${pack.headerClass} ${pack.popular ? 'popular' : ''}`} data-testid={`pack-card-${i}`} intensity={0.2}>
                {pack.popular && <span className="popular-badge">Populaire</span>}
                <div className={`pack-header ${pack.headerClass}`}>
                  <h3>{pack.name}</h3>
                  <div className="pack-price">
                    <span className="price">{pack.price}</span>
                    <span className="period">/ an HT</span>
                  </div>
                </div>
                <div className="pack-content">
                  <div className="features-list">
                    {pack.included.map((feature, j) => (
                      <div key={j} className="feature-item included">
                        <CheckIcon />
                        <span>{feature}</span>
                      </div>
                    ))}
                    {pack.notIncluded.map((feature, j) => (
                      <div key={j} className="feature-item not-included">
                        <XIcon />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button className={`pack-cta ${pack.popular ? 'primary' : 'secondary'}`}>
                    Choisir {pack.name}
                  </button>
                </div>
              </MagneticCard>
            ))}
          </div>

          <p className="pricing-note">Tous les prix sont en CHF, hors taxes. Engagement annuel.</p>
        </div>
      </section>

      {/* Section 8: CTA */}
      <section className="section cta" id="contact" data-testid="cta-section">
        <div className="cta-overlay"></div>
        <div className="container cta-content">
          <h2>Prêt à booster votre visibilité ?</h2>
          <p className="cta-subtitle">Contactez-nous pour discuter de vos objectifs et découvrir comment Lynk peut transformer votre présence digitale.</p>
          
          <div className="cta-buttons">
            <a href="mailto:info@lynk360.ch" className="cta-btn primary">
              <MailIcon />
              <span>info@lynk360.ch</span>
            </a>
            <a href="tel:+41244400015" className="cta-btn secondary">
              <PhoneIcon />
              <span>024 440 00 15</span>
            </a>
          </div>

          <div className="cta-footer">
            <LynkLogo variant="light" />
            <p>Rue des Remparts 7, 1400 Yverdon-les-Bains</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;
